import { useState, useRef } from "react";
import { Button, Image } from "@heroui/react";
import { LuUpload, LuX, LuImage } from "react-icons/lu";
import { ThemedText, ImageLightbox } from "@/components/ui";

/**
 * ImageUploader - Custom image upload component with drag & drop and lightbox
 *
 * @param {Array} images - Array of image objects { file, preview }
 * @param {Function} onChange - Callback when images change
 * @param {Function} onDeleteExisting - Callback when existing server image is deleted
 * @param {number} maxFiles - Maximum number of files (default: 5)
 * @param {string} accept - Accepted file types (default: "image/*")
 */
const ImageUploader = ({
    images = [],
    onChange,
    onDeleteExisting,
    maxFiles = 5,
    accept = "image/*",
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const inputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files).filter((file) =>
            file.type.startsWith("image/"),
        );
        addFiles(files);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        addFiles(files);
        e.target.value = ""; // Reset input
    };

    const addFiles = (files) => {
        const remainingSlots = maxFiles - images.length;
        const filesToAdd = files.slice(0, remainingSlots);

        const newImages = filesToAdd.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
        }));

        onChange([...images, ...newImages]);
    };

    const removeImage = (index) => {
        const imageToRemove = images[index];
        const newImages = [...images];

        // If it's an existing server image, notify parent
        if (imageToRemove.isExisting && imageToRemove.id && onDeleteExisting) {
            onDeleteExisting(imageToRemove.id);
        }

        // Only revoke blob URLs for new uploads, not server URLs
        if (!imageToRemove.isExisting && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
        }

        newImages.splice(index, 1);
        onChange(newImages);
    };

    return (
        <>
            <div className="border-default-300 overflow-hidden rounded-xl border-2 border-dashed">
                {/* Dropzone */}
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                    className={`bg-default-50 flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-3 p-6 transition-all ${
                        isDragging
                            ? "border-primary bg-primary/10"
                            : "hover:bg-default-100"
                    } ${images.length >= maxFiles ? "pointer-events-none opacity-50" : ""}`}
                >
                    <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full ${
                            isDragging ? "bg-primary" : "bg-default-200"
                        }`}
                    >
                        <LuUpload
                            size={20}
                            className={
                                isDragging ? "text-white" : "text-default-500"
                            }
                        />
                    </div>
                    <div className="text-center">
                        <ThemedText className="font-medium">
                            {isDragging
                                ? "Lepaskan file di sini"
                                : "Drag & drop atau klik untuk upload"}
                        </ThemedText>
                        <ThemedText variant="secondary" size="sm">
                            PNG, JPG, JPEG (max {maxFiles} file)
                        </ThemedText>
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                </div>

                {/* Preview Grid */}
                {images.length > 0 && (
                    <div className="border-default-200 border-t p-4">
                        <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6">
                            {images.map((img, index) => (
                                <div
                                    key={index}
                                    className="group border-default-200 relative aspect-square overflow-hidden rounded-lg border transition-all duration-200"
                                >
                                    <Image
                                        src={img.preview}
                                        alt={`Preview ${index + 1}`}
                                        className="h-full w-full cursor-pointer object-cover transition-all duration-200 group-hover:brightness-90"
                                        removeWrapper
                                        onClick={() => setLightboxIndex(index)}
                                    />
                                    {/* Hover Overlay */}
                                    <div className="pointer-events-none absolute inset-0 bg-black/0 transition-all duration-200 group-hover:bg-black/10" />
                                    <Button
                                        type="button"
                                        isIconOnly
                                        size="sm"
                                        color="danger"
                                        variant="solid"
                                        className="absolute top-1 right-1 z-10 opacity-0 transition-opacity group-hover:opacity-100"
                                        onPress={() => removeImage(index)}
                                    >
                                        <LuX size={14} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty state for preview */}
                {images.length === 0 && (
                    <div className="border-default-200 border-t p-4">
                        <div className="bg-default-100 flex items-center gap-2 rounded-lg p-3">
                            <LuImage size={18} className="text-default-400" />
                            <ThemedText variant="secondary" size="sm">
                                Belum ada gambar yang dipilih
                            </ThemedText>
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox using reusable component */}
            <ImageLightbox
                isOpen={lightboxIndex !== null}
                onClose={() => setLightboxIndex(null)}
                images={images}
                initialIndex={lightboxIndex || 0}
                urlKey="preview"
            />
        </>
    );
};

export default ImageUploader;
